import React, { useContext, useEffect, useState } from 'react'
import {
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  Button,
  Table,
  Space,
  Popconfirm,
  Tag,
  Avatar,
  Tooltip
} from 'antd'
import { PlusOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons'
import {
  addAddonGroup,
  updateAddonGroup,
  deleteAddonGroup
} from '../../../Utility/Menu/category.util'

interface AddonGroupModalProps {
  visible: boolean
  onCancel: () => void
  addonGroups: any[]
  onChange: React.Dispatch<React.SetStateAction<any[]>>
  getAddonFoodItems: () => any[]
  resolveFoodItem: (foodId: string) => any
}

const AddonGroupModal: React.FC<AddonGroupModalProps> = ({
  visible,
  onCancel,
  addonGroups,
  onChange,
  getAddonFoodItems,
  resolveFoodItem
}) => {
  const [form] = Form.useForm()
  const [editingGroup, setEditingGroup] = useState<any>(null)
  const [selectedFoodIds, setSelectedFoodIds] = useState<string[]>([])
  const [addons, setAddons] = useState<any[]>([])
  const availableAddonItems = getAddonFoodItems()

  useEffect(() => {
    if (editingGroup) {
      form.setFieldsValue({
        label: editingGroup.label,
        selectionType: editingGroup.selectionType,
        minSelect: editingGroup.minSelect,
        maxSelect: editingGroup.maxSelect
      })
      setAddons(editingGroup.addons || [])
      setSelectedFoodIds([])
    } else {
      form.resetFields()
      setAddons([])
      setSelectedFoodIds([])
    }
  }, [editingGroup, form])

  const handleSave = () => {
    form.validateFields().then((values) => {
      if (addons.length === 0) {
        Modal.error({ content: 'Please add at least one addon to the group' })
        return
      }

      const groupData = {
        ...values,
        addons: addons.map((addon, index) => ({
          addon_id: addon.addon_id,
          isDefault: addon.isDefault || false,
          isActive: addon.isActive !== undefined ? addon.isActive : true,
          sortOrder: index + 1
        }))
      }

      let updatedGroups
      if (editingGroup && editingGroup.group_id) {
        updatedGroups = updateAddonGroup(
          [...addonGroups],
          editingGroup.group_id,
          groupData
        )
      } else {
        updatedGroups = addAddonGroup([...addonGroups], groupData)
      }

      onChange(updatedGroups)
      setEditingGroup(null)
      form.resetFields()
      setAddons([])
      setSelectedFoodIds([])
    })
  }

  const handleDelete = (groupId: string) => {
    const updatedGroups = deleteAddonGroup([...addonGroups], groupId)
    onChange(updatedGroups)
  }

  const handleAddSelectedFoodItems = () => {
    if (selectedFoodIds.length === 0) {
      Modal.warning({ content: 'Please select at least one food item' })
      return
    }

    const newAddons = selectedFoodIds
      .filter((foodId) => !addons.some((a) => a.addon_id === foodId))
      .map((foodId) => ({
        addon_id: foodId,
        isActive: true,
        isDefault: false
      }))

    if (newAddons.length === 0) {
      Modal.warning({ content: 'Selected items are already in the group' })
      return
    }

    setAddons([...addons, ...newAddons])
    setSelectedFoodIds([])
  }

  const handleDeleteAddon = (addon_id: string) => {
    setAddons(addons.filter((a) => a.addon_id !== addon_id))
  }

  const getVegIcon = (type: string) => {
    const style = {
      width: 14,
      height: 14,
      display: 'inline-block',
      marginRight: 8
    }
    if (type === 'VEG')
      return (
        <span style={{ ...style, border: '2px solid green', borderRadius: 2 }}>
          🟢
        </span>
      )
    if (type === 'NONVEG')
      return (
        <span style={{ ...style, border: '2px solid red', borderRadius: 2 }}>
          🔴
        </span>
      )
    return (
      <span style={{ ...style, border: '2px solid orange', borderRadius: 2 }}>
        🟡
      </span>
    )
  }

  const addonColumns = [
    {
      title: 'Food Item',
      dataIndex: 'addon_id',
      key: 'addon_id',
      render: (addon_id: string) => {
        const foodItem = resolveFoodItem(addon_id)
        if (!foodItem) return <Tag color='red'>Not Found</Tag>
        return (
          <Space>
            {foodItem.images?.[0]?.url && (
              <Avatar src={foodItem.images[0].url} size='small' />
            )}
            {getVegIcon(foodItem.type)}
            <span>{foodItem.label}</span>
          </Space>
        )
      }
    },
    {
      title: 'Price',
      dataIndex: 'addon_id',
      key: 'price',
      render: (addon_id: string) => {
        const foodItem = resolveFoodItem(addon_id)
        return foodItem ? (
          <Tooltip title='Price synced from food item'>
            <span>₹{foodItem.price}</span>
          </Tooltip>
        ) : (
          '-'
        )
      }
    },
    {
      title: 'GST %',
      dataIndex: 'addon_id',
      key: 'gstPercentage',
      render: (addon_id: string) => {
        const foodItem = resolveFoodItem(addon_id)
        return foodItem ? (
          <Tooltip title='GST synced from food item'>
            <span>{foodItem.gstPercentage}%</span>
          </Tooltip>
        ) : (
          '-'
        )
      }
    },
    {
      title: 'Cuisine',
      dataIndex: 'addon_id',
      key: 'cuisine',
      render: (addon_id: string) => {
        const foodItem = resolveFoodItem(addon_id)
        return foodItem?.cuisine || '-'
      }
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      )
    },
    {
      title: 'Action',
      key: 'action',
      render: (_: any, record: any) => (
        <Popconfirm
          title='Remove this item from group?'
          onConfirm={() => handleDeleteAddon(record.addon_id)}
        >
          <Button type='link' danger icon={<DeleteOutlined />} />
        </Popconfirm>
      )
    }
  ]

  const groupColumns = [
    {
      title: 'Group Name',
      dataIndex: 'label',
      key: 'label'
    },
    {
      title: 'Type',
      dataIndex: 'selectionType',
      key: 'selectionType',
      render: (type: string) => (
        <Tag color={type === 'SINGLE_SELECT' ? 'blue' : 'purple'}>
          {type === 'SINGLE_SELECT' ? 'Single Select' : 'Multi Select'}
        </Tag>
      )
    },
    {
      title: 'Constraints',
      key: 'constraints',
      render: (_: any, record: any) => (
        <span>
          Min: {record.minSelect}, Max: {record.maxSelect}
        </span>
      )
    },
    {
      title: 'Addon Items',
      key: 'addons',
      render: (_: any, record: any) => {
        const validAddons =
          record.addons?.filter((addon: any) => {
            const foodItem = resolveFoodItem(addon.addon_id)
            return foodItem && foodItem.isAddon
          }) || []
        const invalidCount = (record.addons?.length || 0) - validAddons.length

        return (
          <Space>
            <span>{validAddons.length} items</span>
            {invalidCount > 0 && (
              <Tooltip
                title={`${invalidCount} item(s) no longer marked as addon`}
              >
                <Tag color='warning'>⚠️ {invalidCount}</Tag>
              </Tooltip>
            )}
          </Space>
        )
      }
    },
    {
      title: 'Action',
      key: 'action',
      render: (_: any, record: any) => (
        <Space>
          <Button
            type='link'
            icon={<EditOutlined />}
            onClick={() => setEditingGroup(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title='Delete this addon group?'
            onConfirm={() => handleDelete(record.group_id)}
          >
            <Button type='link' danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ]

  return (
    <Modal
      title='Manage Addon Groups'
      open={visible}
      onCancel={onCancel}
      width={1000}
      footer={null}
    >
      <div style={{ marginBottom: 20 }}>
        <Button
          type='primary'
          icon={<PlusOutlined />}
          onClick={() => setEditingGroup({})}
        >
          Create New Addon Group
        </Button>
      </div>

      {editingGroup !== null && (
        <div
          style={{
            marginBottom: 20,
            padding: 20,
            border: '1px solid #d9d9d9',
            borderRadius: 4
          }}
        >
          <h3>{editingGroup.group_id ? 'Edit' : 'Create'} Addon Group</h3>
          <Form form={form} layout='vertical'>
            <Form.Item
              name='label'
              label='Group Name'
              rules={[{ required: true, message: 'Please enter group name' }]}
            >
              <Input placeholder='e.g., Beverages, Extra Toppings' />
            </Form.Item>

            <Form.Item
              name='selectionType'
              label='Selection Type'
              rules={[
                { required: true, message: 'Please select selection type' }
              ]}
            >
              <Select>
                <Select.Option value='SINGLE_SELECT'>
                  Single Select (Radio)
                </Select.Option>
                <Select.Option value='MULTI_SELECT'>
                  Multi Select (Checkbox)
                </Select.Option>
              </Select>
            </Form.Item>

            <Space>
              <Form.Item
                name='minSelect'
                label='Minimum Selection'
                rules={[{ required: true }]}
              >
                <InputNumber min={0} />
              </Form.Item>

              <Form.Item
                name='maxSelect'
                label='Maximum Selection'
                rules={[{ required: true }]}
              >
                <InputNumber min={1} />
              </Form.Item>
            </Space>

            <div style={{ marginTop: 20, marginBottom: 10 }}>
              <h4>Add Food Items to Group</h4>
              <Space direction='vertical' style={{ width: '100%' }}>
                <Select
                  mode='multiple'
                  placeholder='Search and select food items marked as addons'
                  style={{ width: '100%' }}
                  value={selectedFoodIds}
                  onChange={setSelectedFoodIds}
                  optionLabelProp='label'
                  showSearch
                  filterOption={(input, option: any) => {
                    const foodItem = resolveFoodItem(option.value)
                    if (!foodItem) return false
                    return (
                      foodItem.label
                        .toLowerCase()
                        .includes(input.toLowerCase()) ||
                      foodItem.cuisine
                        ?.toLowerCase()
                        .includes(input.toLowerCase())
                    )
                  }}
                >
                  {availableAddonItems.map((foodItem: any) => (
                    <Select.Option
                      key={foodItem.food_id}
                      value={foodItem.food_id}
                      label={foodItem.label}
                      disabled={addons.some(
                        (a) => a.addon_id === foodItem.food_id
                      )}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          padding: '4px 0'
                        }}
                      >
                        {foodItem.images?.[0]?.url && (
                          <Avatar src={foodItem.images[0].url} size='small' />
                        )}
                        {getVegIcon(foodItem.type)}
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 'bold' }}>
                            {foodItem.label}
                          </div>
                          <div style={{ fontSize: '12px', color: '#666' }}>
                            ₹{foodItem.price} • {foodItem.cuisine} • GST:{' '}
                            {foodItem.gstPercentage}%
                          </div>
                        </div>
                      </div>
                    </Select.Option>
                  ))}
                </Select>
                <Button
                  type='dashed'
                  icon={<PlusOutlined />}
                  onClick={handleAddSelectedFoodItems}
                  disabled={selectedFoodIds.length === 0}
                >
                  Add Selected Items ({selectedFoodIds.length})
                </Button>
              </Space>
            </div>

            {availableAddonItems.length === 0 && (
              <div
                style={{
                  padding: '20px',
                  background: '#fff7e6',
                  border: '1px solid #ffd591',
                  borderRadius: 4,
                  marginBottom: 10
                }}
              >
                ⚠️ No food items marked as addons. Please mark some food items
                as addons first.
              </div>
            )}

            <div style={{ marginTop: 15, marginBottom: 10 }}>
              <h4>Items in this Group ({addons.length})</h4>
            </div>

            <Table
              dataSource={addons}
              columns={addonColumns}
              rowKey='addon_id'
              pagination={false}
              size='small'
            />

            <div style={{ marginTop: 20, textAlign: 'right' }}>
              <Space>
                <Button onClick={() => setEditingGroup(null)}>Cancel</Button>
                <Button type='primary' onClick={handleSave}>
                  {editingGroup.group_id ? 'Update' : 'Create'} Group
                </Button>
              </Space>
            </div>
          </Form>
        </div>
      )}

      <Table
        dataSource={addonGroups}
        columns={groupColumns}
        rowKey='group_id'
        pagination={{ pageSize: 10 }}
      />
    </Modal>
  )
}

export default AddonGroupModal
